import React from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { conflictingSources, needsIntro, needsList, painPoints, searchQuestions, symptoms, systemShows } from '../data/painPoints';
const reveal = {
  initial: {
    opacity: 0,
    y: 16
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }
} as const;
export function PainPoints() {
  return <section className="py-24 px-4 bg-[#F3F6FC]" data-id="element-183" data-mp="1xwgc6udciflt">
      <div className="max-w-4xl mx-auto" data-id="element-184" data-mp="21zlaslub1x56">
        <motion.div {...reveal} className="text-center mb-12" data-id="element-185" data-mp="220o22uvji1lm">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6 text-[#0B1F4D] uppercase" data-id="element-186" data-mp="1o0d4za3mzqs0">
            Still Spending Hours Preparing{' '}
            <span className="text-[#1236C0]" data-id="element-187" data-mp="1t0hq5dfhp9ok">Your Electrical Classes</span>?
          </h2>
          <div className="text-lg text-slate-700 leading-relaxed bg-white p-6 md:p-8 rounded-2xl border border-[#D2DCEC] text-left space-y-4" data-id="element-188" data-mp="3ibi7gb2zlx5r">
            <p data-id="element-189" data-mp="1z05c9f03fd4d">
              {conflictingSources.map(line => <React.Fragment key={line} data-id="element-190" data-mp="1yyc3nu5x6itq">
                  {line}
                  <br data-id="element-191" data-mp="1u12ca8yyzx39" />
                </React.Fragment>)}
            </p>
            <p className="text-[#0B1F4D] font-semibold" data-id="element-192" data-mp="27rp4dia86oqd">
              {symptoms.map((line, index) => <React.Fragment key={line} data-id="element-193" data-mp="23rx0vjzov8rv" data-mpi={index}>
                  {line}
                  {index < symptoms.length - 1 && <br data-id="element-194" data-mp="1g2cess88nved" />}
                </React.Fragment>)}
            </p>
            <p className="text-[#0B1F4D] font-semibold" data-id="element-195" data-mp="1b415ciluvmwo">{needsIntro}</p>
            <p data-id="element-196" data-mp="1p2r5660280oq">
              {needsList.map((line, index) => <React.Fragment key={line} data-id="element-197" data-mp="1p04p1hz3scuw" data-mpi={index}>
                  {line}
                  {index < needsList.length - 1 && <br data-id="element-198" data-mp="30hnwjmjpv1z9" />}
                </React.Fragment>)}
            </p>
            <p data-id="element-199" data-mp="30jh4r69z9u9d">Suddenly, you&apos;re facing the same questions again:</p>
            <ul className="space-y-2 pl-1" data-id="element-200" data-mp="2vi1hd7kbvbnt">
              {searchQuestions.map(question => <li key={question} className="flex items-start gap-3 text-[#0B1F4D] font-semibold" data-id="element-201" data-mp="1t4n4cj8gt3au">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F5C400] shrink-0 mt-3" aria-hidden="true" data-id="element-202" data-mp="1y2ooz8ljhaof" />
                  <span data-id="element-203" data-mp="226mlrcne69eb">{question}</span>
                </li>)}
            </ul>
            <p className="text-[#7A5C00] font-semibold" data-id="element-204" data-mp="2744zrhg9net6">
              You already know how important quality electrical education is.
            </p>
            <p data-id="element-205" data-mp="1efqzsw5ma8ez">But building every class from scratch can become a second job.</p>
          </div>
        </motion.div>

        <motion.div {...reveal} data-id="element-206" data-mp="1jeao337onwai">
          <h3 className="text-xl font-bold text-[#0B1F4D] mb-8 text-center uppercase tracking-wide" data-id="element-207" data-mp="1je0xpeiq7n4a">
            The Real Cost of Teaching Without a Complete System
          </h3>
          <div className="overflow-hidden rounded-xl border border-[#D2DCEC] bg-white shadow-sm shadow-[#0B1F4D]/5" data-id="element-208" data-mp="qpwpomcomxyf">
            {painPoints.map((point, index) => <motion.p key={point} initial={{
            opacity: 0,
            x: -12
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.25,
            delay: Math.min(index * 0.04, 0.3),
            ease: [0.23, 1, 0.32, 1]
          }} className="flex items-start gap-3 px-5 py-4 text-slate-700 text-sm leading-relaxed border-t first:border-t-0 border-[#D2DCEC]" data-id="element-209" data-mp="o6rvmftclny0" data-mpi={index}>
                <X size={16} className="text-[#1236C0] shrink-0 mt-0.5" aria-hidden="true" data-id="element-210" data-mp="5977jayeqteg" />
                {point}
              </motion.p>)}
          </div>

          <div className="text-center mt-12 pt-8 border-t border-[#D2DCEC]" data-id="element-211" data-mp="3rni73a7p8t73">
            <p className="text-2xl font-bold tracking-tight text-[#0B1F4D]" data-id="element-212" data-mp="3mox6vasg69in">
              You don’t need more scattered electrical teaching resources.
            </p>
            <p className="text-xl mt-4 text-slate-700 font-semibold uppercase" data-id="element-213" data-mp="3dnfti06fclxn">
              You need a complete <span className="text-[#1236C0]" data-id="element-214" data-mp="1hkb40q2vkfq9">professional system</span> that shows
              you:
            </p>
            <ul className="mt-5 space-y-1.5" data-id="element-215" data-mp="1llwbtx0jy3tl">
              {systemShows.map(line => <li key={line} className="text-lg md:text-xl font-extrabold tracking-wide text-[#0B1F4D]" data-id="element-216" data-mp="17url5kf5wlve">
                  {line}
                </li>)}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>;
}